import { useEffect } from 'react';

/**
 * Hook to prevent body scroll when modal is open (for desktop AND mobile)
 * Unlike popovers, modals need simple overflow:hidden approach
 * NO touchmove listeners - let Ant Design Modal handle internal content scrolling
 */
export const usePreventBodyScroll = (isOpen) => {
  useEffect(() => {
    if (!isOpen) return;

    const html = document.documentElement;
    const body = document.body;
    
    // Store original values
    const originalHTMLOverflow = html.style.overflow;
    const originalBodyOverflow = body.style.overflow;

    // Apply scroll lock to both html and body for better compatibility
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';

    return () => {
      // Restore original values
      html.style.overflow = originalHTMLOverflow;
      body.style.overflow = originalBodyOverflow;
    };
  }, [isOpen]);
};

/**
 * Automatically prevent body scroll when Ant Design dropdowns/popovers are open on mobile.
 * Uses overflow:hidden + touchmove prevention (NO position:fixed to avoid layout shifts).
 */
export const useAutoPreventBodyScroll = () => {
  useEffect(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (!isMobile) return;

    let locked = false;

    const preventBodyTouch = (e) => {
      // Allow scrolling inside interactive containers
      if (e.target.closest(
        '.ant-select-dropdown, .ant-picker-dropdown, .ant-dropdown, .ant-popover-inner, .ant-modal-content, .ant-select, .ant-modal-wrap'
      )) return;
      e.preventDefault();
    };

    const lock = () => {
      if (locked) return;
      locked = true;
      document.body.style.overflow = 'hidden';
      document.addEventListener('touchmove', preventBodyTouch, { passive: false });
    };

    const unlock = () => {
      if (!locked) return;
      locked = false;
      document.body.style.overflow = '';
      document.removeEventListener('touchmove', preventBodyTouch);
    };

    const observer = new MutationObserver(() => {
      const hasPopover = document.querySelector(
        '.ant-popover:not(.ant-popover-hidden)'
      );
      if (hasPopover) {
        lock();
      } else {
        unlock();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      observer.disconnect();
      unlock();
    };
  }, []);
};
